import React, { useState, useEffect, useRef } from "react";
import { db, ref, onValue } from "./firebase";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/swiper-bundle.css";
import { HOLIDAY_PRESETS, FIREBASE_HOLIDAY_PATH } from "./constants/holidayPresets";
import { X } from "lucide-react";

// Initialize Swiper modules
SwiperCore.use([Navigation, Pagination]);

// Confetti canvas for holiday banner
function Confetti({ colors, active }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    if (!active || !colors?.length) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const w = (canvas.width = window.innerWidth);
    const h = (canvas.height = window.innerHeight);
    const count = 55;

    const create = () => {
      const res = [];
      for (let i = 0; i < count; i++) {
        res.push({
          x: Math.random() * w,
          y: -20,
          r: 4 + Math.random() * 8,
          color: colors[Math.floor(Math.random() * colors.length)],
          vy: 2 + Math.random() * 4,
          vx: (Math.random() - 0.5) * 2,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 10,
        });
      }
      return res;
    };

    particlesRef.current = create();
    const startRef = { current: Date.now() };

    const animate = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      if (elapsed > 5) return;
      ctx.clearRect(0, 0, w, h);
      particlesRef.current.forEach((p) => {
        p.y += p.vy;
        p.x += p.vx;
        p.vy += 0.05;
        p.rotation += p.rotationSpeed;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 1.5);
        ctx.restore();
      });
      frameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [active, colors]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-30"
      aria-hidden
    />
  );
}

const Dashboard = () => {
  const [courses, setCourses] = useState({});
  const [homeworks, setHomeworks] = useState({});
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [holidayGreeting, setHolidayGreeting] = useState(null);
  const [holidayBannerDismissed, setHolidayBannerDismissed] = useState(false);

  // Fetch data from Firebase
  useEffect(() => {
    // Fetch all courses and their data
    const coursesRef = ref(db, "courses");
    onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        const coursesData = snapshot.val();
        setCourses(coursesData);

        // Fetch homeworks for each course (only due assignments)
        const homeworksData = {};
        const now = new Date();

        Object.entries(coursesData).forEach(([course, data]) => {
          if (data.homeworks) {
            const dueHomeworks = Object.values(data.homeworks).filter(hw => {
              if (!hw.dueDate) return false;
              const dueDate = new Date(hw.dueDate);
              return dueDate >= now;
            });
            homeworksData[course] = dueHomeworks;
          }
        });

        setHomeworks(homeworksData);
      }
    });

    // Fetch registration requests
    const registrationRef = ref(db, "registrationRequests");
    onValue(registrationRef, (snapshot) => {
      if (snapshot.exists()) {
        const requests = Object.entries(snapshot.val()).map(([id, data]) => ({
          id,
          ...data
        }));
        setRegistrationRequests(requests);
      } else {
        setRegistrationRequests([]);
      }
    });

    // Holiday greeting (same as student dashboard)
    const holidayRef = ref(db, FIREBASE_HOLIDAY_PATH);
    const unsubHoliday = onValue(holidayRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        if (data.enabled && data.holidayId) setHolidayGreeting(data);
        else setHolidayGreeting(null);
      } else setHolidayGreeting(null);
    });
    return () => unsubHoliday();
  }, []);

  const holidayPreset = holidayGreeting ? HOLIDAY_PRESETS.find((p) => p.id === holidayGreeting.holidayId) : null;
  const showHolidayBanner = holidayPreset && !holidayBannerDismissed;
  const holidayImageUrl = holidayPreset?.image
    ? `${process.env.PUBLIC_URL || ""}${holidayPreset.image}`
    : null;

  return (
    <div className="p-6 relative">
      <Confetti colors={holidayPreset?.confettiColors} active={showHolidayBanner} />

      {/* Holiday greeting banner */}
      {showHolidayBanner && (
        <div
          className="relative z-40 mb-6 rounded-xl border overflow-hidden shadow-lg"
          style={{
            background: `linear-gradient(135deg, ${holidayPreset.confettiColors[0]}22 0%, ${holidayPreset.confettiColors[1] ?? holidayPreset.confettiColors[0]}33 50%, ${holidayPreset.confettiColors[2] ?? holidayPreset.confettiColors[0]}22 100%)`,
            borderColor: `${holidayPreset.confettiColors[0]}44`,
          }}
        >
          <div className="flex items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-center gap-4 min-w-0">
              {holidayImageUrl ? (
                <img
                  src={holidayImageUrl}
                  alt=""
                  className="h-16 w-16 object-contain shrink-0 rounded-lg bg-white/30"
                  onError={(e) => {
                    e.target.style.display = "none";
                    const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = "block";
                  }}
                />
              ) : null}
              <span
                className="text-4xl shrink-0"
                style={{ display: holidayImageUrl ? "none" : "block" }}
                role="img"
                aria-hidden
              >
                {holidayPreset.fallbackEmoji}
              </span>
              <h2 className="text-xl font-bold text-gray-900 truncate">{holidayPreset.label}</h2>
            </div>
            <button
              type="button"
              onClick={() => setHolidayBannerDismissed(true)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-white/30 transition-colors shrink-0"
              aria-label="Dismiss banner"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      <h1 className="text-3xl font-bold mb-6 text-gray-900">Admin Dashboard</h1>

      {/* Grid Layout for Dashboard Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Box 1: Syllabus Completion for All Courses */}
        <div className="bg-white p-6 shadow-lg rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Syllabus Completion</h3>
          <div className="relative">
            <Swiper
              spaceBetween={30}
              slidesPerView={1}
              navigation={{
                nextEl: '.swiper-button-next-syllabus',
                prevEl: '.swiper-button-prev-syllabus',
              }}
              pagination={{ clickable: true, el: ".custom-pagination-syllabus" }}
            >
              {Object.entries(courses)
                .sort(([, a], [, b]) => a.index - b.index)
                .map(([course, data]) => {
                  const completedChapters = Object.values(data.chapters || {}).filter((ch) => ch.done).length;
                  const totalChapters = Object.keys(data.chapters || {}).length;
                  const completionPercentage = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;

                  return (
                    <SwiperSlide key={course}>
                      <div className="flex flex-col px-8">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="text-md font-semibold text-gray-900">{course}</h4>
                          <span className="text-sm font-medium text-blue-600">{completionPercentage}%</span>
                        </div>
                        <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-600 rounded-full transition-all duration-500"
                            style={{ width: `${completionPercentage}%` }}
                          />
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          {completedChapters} of {totalChapters} chapters completed
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
            </Swiper>
            <div className="swiper-button-prev-syllabus absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-50">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="swiper-button-next-syllabus absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-50">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="custom-pagination-syllabus mt-4 flex justify-center space-x-2"></div>
          </div>
        </div>

        {/* Box 2: Due Assignments for All Courses */}
        <div className="bg-white p-6 shadow-lg rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Due Assignments</h3>
          <div className="relative">
            <Swiper
              spaceBetween={30}
              slidesPerView={1}
              navigation={{
                nextEl: '.swiper-button-next-assignments',
                prevEl: '.swiper-button-prev-assignments',
              }}
              pagination={{ clickable: true, el: ".custom-pagination-assignments" }}
            >
              {Object.entries(homeworks).map(([course, assignments]) => (
                <SwiperSlide key={course}>
                  <div className="flex flex-col px-8">
                    <h4 className="text-xl font-semibold mb-4 text-gray-900">{course}</h4>
                    <div className="space-y-3">
                      {assignments.length > 0 ? (
                        assignments.map((hw, index) => (
                          <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-gray-900">{hw.title}</span>
                              <span className="text-sm text-gray-600">{new Date(hw.dueDate).toLocaleDateString()}</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500 rounded-full"
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4">No assignments due.</p>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="swiper-button-prev-assignments absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-50">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="swiper-button-next-assignments absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-50">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="custom-pagination-assignments mt-4 flex justify-center space-x-2"></div>
          </div>
        </div>

        {/* Box 3: Registration Requests */}
        <div className="bg-white p-6 shadow-lg rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Registration Requests</h3>
          <div className="flex flex-col">
            <div className="text-5xl font-bold text-blue-600 mb-2">
              {registrationRequests.length}
            </div>
            <p className="text-gray-600 mb-4">Pending registrations</p>
            <div className="w-full">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Course</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrationRequests.slice(0, 3).map((request) => (
                      <tr key={request.id} className="border-t border-gray-100">
                        <td className="px-4 py-2 text-sm text-gray-700">{request.name}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{request.course}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${request.status === "Pending" ? "bg-yellow-100 text-yellow-800" :
                            request.status === "Registered" ? "bg-green-100 text-green-800" :
                              request.status === "Contacted" ? "bg-blue-100 text-blue-800" :
                                "bg-red-100 text-red-800"
                            }`}>
                            {request.status || "Pending"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {registrationRequests.length > 3 && (
                <p className="text-sm text-gray-500 mt-2">
                  + {registrationRequests.length - 3} more requests
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;